import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Wallet, TrendingDown, Receipt, Loader2, BarChart2 } from 'lucide-react';
import { subDays, format } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/page-header';
import { ExpenseTable } from '@/components/expenses/expense-table';
import { ExpenseFilters } from '@/components/expenses/expense-filters';
import { CreateExpenseDialog } from '@/components/expenses/create-expense-dialog';
import { useExpenses } from '@/hooks/use-expenses';
import { useExpenseByCategory } from '@/hooks/use-reports';
import { formatCurrency } from '@/lib/utils';

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-semibold">{label}</p>
        <p className="text-primary mt-1">
          {formatCurrency(payload[0].value)}
        </p>
      </div>
    );
  }
  return null;
};

export const Route = createFileRoute('/expenses')({
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <ExpensesPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function ExpensesPage() {
  const [params, setParams] = useState<{
    startDate?: string;
    endDate?: string;
    category?: string;
  }>({});

  const { data: expensesRes, isLoading: isLoadingExpenses } = useExpenses(params);
  const expenses = expensesRes?.data || [];

  const { data: categoryData, isLoading: isLoadingChart } = useExpenseByCategory(
    params.startDate || format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    params.endDate || format(new Date(), 'yyyy-MM-dd')
  );

  const totalExpense = expenses.reduce((sum, exp) => sum + Number(exp.amount), 0);

  const handleCategoryChange = (category: string) => {
    setParams(prev => ({ ...prev, category }));
  };

  const handleDateRangeChange = (startDate?: string, endDate?: string) => {
    setParams(prev => ({ ...prev, startDate, endDate }));
  };

  const handleReset = () => {
    setParams({});
  };

  return (
    <div className="flex flex-col gap-6 min-w-0 max-w-full">
      <PageHeader
        title="Expenses"
        actions={<CreateExpenseDialog />}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="relative overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Total Period Expense
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-500">
              {formatCurrency(totalExpense)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Based on {expenses.length} transaction{expenses.length !== 1 ? 's' : ''}
            </p>
          </CardContent>
          <TrendingDown className="absolute -bottom-2 -right-2 h-20 w-20 text-red-500/10" />
        </Card>

        <Card className="relative overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Selected Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold uppercase">
              {params.category || 'ALL'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Showing filtered results
            </p>
          </CardContent>
          <Receipt className="absolute -bottom-2 -right-2 h-20 w-20 text-primary/10" />
        </Card>

        <Card className="relative overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {expenses.length}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
               <Wallet className="h-3 w-3" />
               Latest active records
            </div>
          </CardContent>
          <Receipt className="absolute -bottom-2 -right-2 h-20 w-20 text-primary/10" />
        </Card>
      </div>

      <Card className="border shadow-sm">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle>Expense Analytics</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Spending by category for the selected period</p>
          </div>
          <div className="bg-primary/10 p-2 rounded-lg">
            <BarChart2 className="h-5 w-5 text-primary" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
            {/* Bar Chart */}
            <div className="h-[300px] w-full border rounded-xl p-4 bg-muted/5">
              <h4 className="text-xs font-bold uppercase text-muted-foreground mb-4">Total Spending by Category</h4>
              {isLoadingChart ? (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted" />
                </div>
              ) : categoryData && categoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryData} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                    <XAxis 
                      dataKey="category" 
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 10, fill: 'currentColor' }}
                      className="text-muted-foreground"
                    />
                    <YAxis 
                      tickFormatter={(value) => `Rp ${value/1000}k`}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 10, fill: 'currentColor' }}
                      className="text-muted-foreground"
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                    <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                      {categoryData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} className="fill-primary" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
                  <BarChart2 className="h-12 w-12 mb-2 opacity-20" />
                  <p className="text-sm">No data</p>
                </div>
              )}
            </div>

            {/* Pie Chart / Distribution */}
            <div className="h-[300px] w-full border rounded-xl p-4 bg-muted/5">
              <h4 className="text-xs font-bold uppercase text-muted-foreground mb-4">Percentage Distribution</h4>
              {isLoadingChart ? (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted" />
                </div>
              ) : categoryData && categoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="amount"
                      nameKey="category"
                    >
                      {categoryData.map((entry: any, index: number) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={[
                            'hsl(var(--primary))', 
                            '#f59e0b', 
                            '#ef4444', 
                            '#10b981', 
                            '#6366f1', 
                            '#f43f5e'
                          ][index % 6]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
                  <BarChart2 className="h-12 w-12 mb-2 opacity-20" />
                  <p className="text-sm">No data</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-none shadow-none bg-transparent">
        <CardContent className="p-0 space-y-4">
          <div className="bg-card p-4 rounded-xl border">
            <ExpenseFilters
              category={params.category}
              startDate={params.startDate}
              endDate={params.endDate}
              onCategoryChange={handleCategoryChange}
              onDateRangeChange={handleDateRangeChange}
              onReset={handleReset}
            />
          </div>

          <div className={isLoadingExpenses ? 'opacity-50 pointer-events-none' : ''}>
            <ExpenseTable expenses={expenses} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
